import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import { FeatureFlagState } from '../../interfaces/misc/feature-flag';
import { ApiService } from '../api/api.service';
import { LocalStorageService } from '../local-storage/local-storage.service';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly stateSubject = new BehaviorSubject<FeatureFlagState>({
    status: 'loading', companyCode: null, flags: [],
  });
  readonly resolved$ = this.stateSubject.asObservable();

  constructor(private api: ApiService, private storage: LocalStorageService) {}

  get snapshot(): FeatureFlagState {
    return this.stateSubject.value;
  }

  loadForCurrentCompany(force = false): Observable<FeatureFlagState> {
    const companyCode = this.storage.get('companyCode').trim().toUpperCase();
    const current = this.snapshot;
    if (!force && current.status === 'resolved' && current.companyCode === companyCode) {
      return of(current);
    }
    if (!companyCode) {
      const error: FeatureFlagState = { status: 'error', companyCode: null, flags: [] };
      this.stateSubject.next(error);
      return of(error);
    }

    this.stateSubject.next({ status: 'loading', companyCode, flags: [] });
    return this.api.getFeatureFlags(companyCode).pipe(
      map(flags => ({ status: 'resolved', companyCode, flags } as FeatureFlagState)),
      tap(state => this.stateSubject.next(state)),
      catchError(() => {
        const error: FeatureFlagState = { status: 'error', companyCode, flags: [] };
        this.stateSubject.next(error);
        return of(error);
      }),
    );
  }

  refresh(): Observable<FeatureFlagState> {
    return this.loadForCurrentCompany(true);
  }

  isEnabled(featureName: string): boolean {
    const state = this.snapshot;
    if (state.status !== 'resolved') return false;
    const normalized = featureName.trim().toLowerCase();
    return state.flags.some(flag => flag.featureName.trim().toLowerCase() === normalized && flag.isEnabled);
  }

  isConfigured(featureName: string): boolean {
    const state = this.snapshot;
    if (state.status !== 'resolved') return false;
    const normalized = featureName.trim().toLowerCase();
    return state.flags.some(flag => flag.featureName.trim().toLowerCase() === normalized);
  }
}
